import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ShipmentFlowConfig,
  ShipmentCreationStep,
  ShipmentBasicDetailsData,
  ShipmentCargoStepData,
  ShipmentParty,
  ShipmentPartyType,
  ShipmentSyncStatus,
  ShipmentSaveStep,
  ShipmentCreationState,
} from './types';
import { createEmptyCargoLine } from './utils/cargoLines';

function createInitialState(): ShipmentCreationState {
  return {
    currentStep: 'basics',
    formData: {
      basicDetails: {
        poNumbers: [],
        incoterm: null,
        incotermLocation: null,
        loadingReference: null,
        unloadingReference: null,
        generalRemarks: null,
        referenceNumber: null,
        areGoodsStackable: false,
        isPalletExchangeRequiredAtPickup: false,
        isPalletExchangeRequiredAtDelivery: false,
        requiresTemperatureControl: false,
        minTemperatureC: null,
        maxTemperatureC: null,
        sender: null,
        receiver: null,
        loadingAddress: null,
        unloadingAddress: null,
        warehouseId: null,
        warehouseName: null,
        warehouseRemarks: null,
        loadingDate: null,
        unloadingDate: null,
        loadingTimeFrom: null,
        loadingTimeTo: null,
        unloadingTimeFrom: null,
        unloadingTimeTo: null,
      },
      cargo: {
        lines: [createEmptyCargoLine()],
      },
    },
    validation: { showErrors: false },
    isDirty: false,
    sync: {
      status: 'idle',
      shipmentId: null,
      error: null,
      errorDescription: null,
      failedStep: null,
    },
  };
}

export interface ShipmentSliceSelectors {
  selectRoot: (state: never) => ShipmentCreationState;
  selectCurrentStep: (state: never) => ShipmentCreationStep;
  selectFormData: (state: never) => ShipmentCreationState['formData'];
  selectBasicDetails: (state: never) => ShipmentBasicDetailsData;
  selectCargo: (state: never) => ShipmentCargoStepData;
  selectValidation: (state: never) => ShipmentCreationState['validation'];
  selectShowErrors: (state: never) => boolean;
  selectSync: (state: never) => ShipmentCreationState['sync'];
  selectIsDirty: (state: never) => boolean;
  selectShipmentId: (state: never) => string | null;
}

export function createShipmentCreationSlice(config: ShipmentFlowConfig) {
  const initialState = createInitialState();

  const slice = createSlice({
    name: config.sliceName,
    initialState,
    reducers: {
      setStep: (state, action: PayloadAction<ShipmentCreationStep>) => {
        state.currentStep = action.payload;
      },

      goToNextStep: (state) => {
        const currentIndex = config.stepOrder.indexOf(state.currentStep);
        if (currentIndex < config.stepOrder.length - 1) {
          state.currentStep = config.stepOrder[currentIndex + 1];
          state.validation.showErrors = false;
        }
      },

      goToPreviousStep: (state) => {
        const currentIndex = config.stepOrder.indexOf(state.currentStep);
        if (currentIndex > 0) {
          state.currentStep = config.stepOrder[currentIndex - 1];
          state.validation.showErrors = false;
        }
      },

      updateBasicDetails: (state, action: PayloadAction<Partial<ShipmentBasicDetailsData>>) => {
        state.formData.basicDetails = {
          ...state.formData.basicDetails,
          ...action.payload,
        };
        state.isDirty = true;
      },

      updateParty: (
        state,
        action: PayloadAction<{ type: ShipmentPartyType; data: Partial<ShipmentParty> }>,
      ) => {
        const { type, data } = action.payload;
        const currentParty = state.formData.basicDetails[type];
        state.formData.basicDetails[type] = {
          ...currentParty,
          ...data,
        } as ShipmentParty;
        state.isDirty = true;
      },

      clearParty: (state, action: PayloadAction<ShipmentPartyType>) => {
        state.formData.basicDetails[action.payload] = null;
        state.isDirty = true;
      },

      updateCargo: (state, action: PayloadAction<Partial<ShipmentCargoStepData>>) => {
        state.formData.cargo = {
          ...state.formData.cargo,
          ...action.payload,
        };
        state.isDirty = true;
      },

      /** Swap a client-temp cargo line id for the server good id after POST. */
      replaceCargoLineId: (
        state,
        action: PayloadAction<{ fromId: string; toId: string }>,
      ) => {
        const { fromId, toId } = action.payload;
        state.formData.cargo.lines = (state.formData.cargo.lines ?? []).map((line) =>
          line.id === fromId ? { ...line, id: toId } : line,
        );
      },

      triggerValidation: (state) => {
        state.validation.showErrors = true;
      },

      hideValidation: (state) => {
        state.validation.showErrors = false;
      },

      resetForm: () => createInitialState(),

      loadDraftData: (
        state,
        action: PayloadAction<{
          shipmentId: string;
          basicDetails?: Partial<ShipmentBasicDetailsData>;
          cargo?: Partial<ShipmentCargoStepData>;
        }>,
      ) => {
        const { shipmentId, basicDetails, cargo } = action.payload;
        state.sync.shipmentId = shipmentId;

        if (basicDetails) {
          state.formData.basicDetails = {
            ...state.formData.basicDetails,
            ...basicDetails,
          };
        }

        if (cargo) {
          state.formData.cargo = {
            ...state.formData.cargo,
            ...cargo,
          };
        }

        state.isDirty = false;
      },

      setSyncStatus: (state, action: PayloadAction<ShipmentSyncStatus>) => {
        state.sync.status = action.payload;
      },

      setShipmentId: (state, action: PayloadAction<string>) => {
        state.sync.shipmentId = action.payload;
      },

      /**
       * Open a save run. Clears the previous run's outcome so a retry reports what this
       * attempt achieved rather than what the last one did.
       */
      beginSave: (state) => {
        state.sync.status = 'saving';
        state.sync.error = null;
        state.sync.errorDescription = null;
        state.sync.failedStep = null;
      },

      setSyncError: (state, action: PayloadAction<string | null>) => {
        state.sync.error = action.payload;
        state.sync.errorDescription = null;
        state.sync.failedStep = null;
        if (action.payload) {
          state.sync.status = 'error';
        }
      },

      setSyncErrorWithStep: (
        state,
        action: PayloadAction<{
          step: ShipmentSaveStep;
          message: string;
          description?: string;
        }>,
      ) => {
        state.sync.error = action.payload.message;
        state.sync.errorDescription = action.payload.description ?? null;
        state.sync.failedStep = action.payload.step;
        state.sync.status = 'error';
      },

      clearSyncState: (state) => {
        state.sync.status = 'idle';
        state.sync.error = null;
        state.sync.errorDescription = null;
        state.sync.failedStep = null;
      },
    },
  });

  // Use Record<string, unknown> to avoid circular dependency with RootState
  const selectRoot = (state: Record<string, unknown>) =>
    state[config.sliceName] as ShipmentCreationState;

  const selectors: ShipmentSliceSelectors = {
    selectRoot: selectRoot as ShipmentSliceSelectors['selectRoot'],
    selectCurrentStep: ((state: Record<string, unknown>) => selectRoot(state).currentStep) as ShipmentSliceSelectors['selectCurrentStep'],
    selectFormData: ((state: Record<string, unknown>) => selectRoot(state).formData) as ShipmentSliceSelectors['selectFormData'],
    selectBasicDetails: ((state: Record<string, unknown>) => selectRoot(state).formData.basicDetails) as ShipmentSliceSelectors['selectBasicDetails'],
    selectCargo: ((state: Record<string, unknown>) => selectRoot(state).formData.cargo) as ShipmentSliceSelectors['selectCargo'],
    selectValidation: ((state: Record<string, unknown>) => selectRoot(state).validation) as ShipmentSliceSelectors['selectValidation'],
    selectShowErrors: ((state: Record<string, unknown>) => selectRoot(state).validation.showErrors) as ShipmentSliceSelectors['selectShowErrors'],
    selectSync: ((state: Record<string, unknown>) => selectRoot(state).sync) as ShipmentSliceSelectors['selectSync'],
    selectIsDirty: ((state: Record<string, unknown>) => selectRoot(state).isDirty) as ShipmentSliceSelectors['selectIsDirty'],
    selectShipmentId: ((state: Record<string, unknown>) => selectRoot(state).sync.shipmentId) as ShipmentSliceSelectors['selectShipmentId'],
  };

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    selectors,
  };
}

export type ShipmentSliceActions = ReturnType<typeof createShipmentCreationSlice>['actions'];
